<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <header class="modal-header">
        <h3>Pace wizard</h3>
        <button class="close-btn" @click="close" aria-label="Close">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </header>

      <div class="modal-body">
        <template v-if="step < QUESTIONS.length">
          <p class="text-muted mb-1 step-label">Question {{ step + 1 }} of {{ QUESTIONS.length }}</p>
          <p class="question-text">{{ QUESTIONS[step].question }}</p>
          <div class="option-list">
            <button
              v-for="option in QUESTIONS[step].options"
              :key="option.key"
              type="button"
              class="option-btn"
              :class="{ active: answers[QUESTIONS[step].key] === option.key }"
              @click="choose(QUESTIONS[step].key, option.key)"
            >
              <span>{{ option.label }}</span>
              <span class="option-btn__chevron" aria-hidden="true">›</span>
            </button>
          </div>
        </template>

        <template v-else>
          <p class="text-muted mb-1 step-label">Suggested plan</p>
          <p class="result-days">{{ suggestedDays }} {{ suggestedDays === 1 ? 'day' : 'days' }}</p>
          <p class="result-detail">
            At this pace, {{ formattedDistance }} with {{ formattedElevation }} of ascent works out to about
            {{ formattedHours }} of hiking, spread across {{ hoursPerDay }}h days.
          </p>
        </template>
      </div>

      <footer class="modal-footer">
        <button v-if="step > 0" class="btn btn-secondary" @click="back">Back</button>
        <button v-else class="btn btn-secondary" @click="close">Cancel</button>
        <button
          v-if="step < QUESTIONS.length"
          class="btn btn-primary"
          :disabled="!answers[QUESTIONS[step].key]"
          @click="next"
        >
          Next
        </button>
        <button v-else class="btn btn-primary" @click="apply">Apply {{ suggestedDays }}-day plan</button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { HikeTimeUtility } from '../../../utils/helpers/HikeTime';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { MIN_GUIDED_DAYS, MAX_GUIDED_DAYS } from '../utils/masterTrail';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  distanceMeters: {
    type: Number,
    default: 0,
  },
  elevationGainMeters: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'apply']);

// Three quick multiple-choice questions — mirrors hike-planner's PaceWizard,
// but the answers feed HiiKER's own HikeTimeUtility formula rather than a
// bespoke Naismith calculation.
const QUESTIONS = [
  {
    key: 'flat',
    question: 'On flat ground, how fast do you walk?',
    options: [
      { key: 'fourKmPerHour', label: 'Leisurely (4 km/h)' },
      { key: 'fiveKmPerHour', label: 'Steady (5 km/h)' },
      { key: 'sixKmPerHour', label: 'Brisk (6 km/h)' },
    ],
  },
  {
    key: 'climb',
    question: 'How do you handle climbs?',
    options: [
      { key: 'fiveHundredMetersPerHour', label: 'Slow and steady (500 m/h)' },
      { key: 'sixHundredMetersPerHour', label: 'Average (600 m/h)' },
      { key: 'eightHundredMetersPerHour', label: 'Strong (800 m/h)' },
    ],
  },
  {
    key: 'hours',
    question: 'How many hours a day do you want to hike?',
    options: [
      { key: '4.5', label: 'Short days (~4.5h)' },
      { key: '6.5', label: 'Full days (~6.5h)' },
      { key: '8.5', label: 'Long days (~8.5h)' },
    ],
  },
];

const step = ref(0);
const answers = ref({ flat: null, climb: null, hours: null });

watch(() => props.isOpen, (open) => {
  if (open) {
    step.value = 0;
    answers.value = { flat: null, climb: null, hours: null };
  }
});

function choose(key, value) {
  answers.value[key] = value;
}

function next() {
  if (step.value < QUESTIONS.length) step.value += 1;
}

function back() {
  step.value = Math.max(0, step.value - 1);
}

const hoursPerDay = computed(() => parseFloat(answers.value.hours) || 6.5);

const suggestedHoursTotal = computed(() => {
  const paceUtility = HikeTimeUtility.fromPair(answers.value.flat || 'fiveKmPerHour', answers.value.climb || 'sixHundredMetersPerHour');
  const totalSeconds = paceUtility.secondsDuration(props.elevationGainMeters, props.distanceMeters);
  return totalSeconds / 3600;
});

const suggestedDays = computed(() => {
  const raw = Math.ceil(suggestedHoursTotal.value / hoursPerDay.value);
  return Math.min(MAX_GUIDED_DAYS, Math.max(MIN_GUIDED_DAYS, raw || MIN_GUIDED_DAYS));
});

const formattedDistance = computed(() => MeasurementPrefUtility.preference().smallUnitFormatter(props.distanceMeters, true));
const formattedElevation = computed(() => MeasurementPrefUtility.preference().smallUnitFormatter(props.elevationGainMeters, false));
const formattedHours = computed(() => `${suggestedHoursTotal.value.toFixed(1)}h`);

function handleOverlayClick(event) {
  if (event.target === event.currentTarget) close();
}

function close() {
  emit('close');
}

function apply() {
  emit('apply', suggestedDays.value);
  close();
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--dark);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #fff;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.step-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #a1a4ad;
}

.question-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0.35rem 0 1rem;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-align: left;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.option-btn__chevron {
  color: #6b6d76;
  font-size: 1.1rem;
  line-height: 1;
  flex-shrink: 0;
}

.option-btn:hover {
  border-color: rgba(253, 223, 0, 0.55);
}

.option-btn.active {
  border-color: #fddf00;
  background-color: rgba(253, 223, 0, 0.1);
  color: #fddf00;
}

.option-btn.active .option-btn__chevron {
  color: #fddf00;
}

.result-days {
  font-size: 2.75rem;
  font-weight: 800;
  color: #fddf00;
  line-height: 1;
  margin: 0.25rem 0 0.6rem;
}

.result-detail {
  color: #a1a4ad;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border: none;
}
</style>
